import React, { useMemo, useState } from "react";
import { Plus, ExternalLink, Ruler, Star } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { mediaUrl } from "@/lib/api";
import { ShareButton } from "@/components/site/ShareButton";
import { COLOR_HEX, COLOR_PRESETS, SIZE_PRESETS } from "@/admin/editors";

// A product can have:
//  - `variants: [{ code, label, price }]`  → size chips with per-size pricing
//  - `colors: [string]`                    → color chips; empty → every color in the category palette
//  - fallback `sizes: [string]`            → legacy per-size labels reusing the flat `price`
export const MerchCard = ({ product, showSizes = false }) => {
  const { add } = useCart();

  const variants = useMemo(() => {
    let list = [];
    if (Array.isArray(product.variants) && product.variants.length > 0) list = product.variants;
    else if (Array.isArray(product.sizes) && product.sizes.length > 0) {
      list = product.sizes.map((s) => ({ code: s, label: s, price: product.price }));
    }
    const order = (SIZE_PRESETS[product.category] || []).map((o) => o.code);
    if (!order.length) return list;
    const idx = (c) => (order.indexOf(c) === -1 ? 999 : order.indexOf(c));
    return [...list].sort((a, b) => idx(a.code) - idx(b.code));
  }, [product.variants, product.sizes, product.price, product.category]);

  // Palette categories always show every color for that product type
  const colors = COLOR_PRESETS[product.category]
    || (Array.isArray(product.colors) ? product.colors : []);

  const [selectedCode, setSelectedCode] = useState(variants[0]?.code || null);
  const [selectedColor, setSelectedColor] = useState(colors[0] || null);
  const selected = variants.find((v) => v.code === selectedCode) || null;
  const displayPrice = selected ? selected.price : product.price;

  const img = mediaUrl(product.image_url) || product.image || "/icon-512.png";
  const isExternal = Boolean(product.fulfillment_url);

  const onAdd = () => {
    // Build a unique cart line id from size + color so the same product in a different
    // size or color becomes its own line with its own quantity.
    const parts = [product.id, selected?.code, selectedColor].filter(Boolean);
    const lineId = parts.join("-");
    const nameSuffix = [selected?.label, selectedColor].filter(Boolean).join(" · ");
    const cartItem = {
      id: lineId,
      merch_id: product.id,
      size: selected ? selected.label : null,
      color: selectedColor || null,
      name: product.name + (nameSuffix ? ` (${nameSuffix})` : ""),
      price: displayPrice,
      image: img,
    };
    add(cartItem);
    toast.success(`${product.name} added to cart`);
  };

  const showVariants = showSizes && variants.length > 0;
  const showColors = showSizes && colors.length > 0;
  const uniquePrices = new Set(variants.map((v) => Number(v.price)));
  const startingFrom = variants.length > 0 && uniquePrices.size > 1;

  return (
    <div className="frame p-3 flex flex-col group" style={{ backgroundColor: "var(--ink-raised)" }} data-testid={`merch-card-${product.id}`}>
      <div className="relative overflow-hidden" style={{ borderRadius: "var(--radius)", background: "radial-gradient(120% 90% at 50% 0%, rgba(200,155,60,.14), #000 65%)" }}>
        <div className="aspect-square grid place-items-center p-6">
          <img src={img} alt={product.name} className="max-h-full max-w-full object-contain transition-transform duration-700 group-hover:scale-105" style={{ filter: "drop-shadow(0 20px 40px rgba(0,0,0,.6))" }} />
        </div>
        {product.category && (
          <span className="absolute top-3 left-3 font-caps" style={{ fontSize: ".5rem", color: "var(--gold)", border: "1px solid rgba(200,155,60,.4)", padding: ".25rem .5rem", borderRadius: "var(--radius)", background: "rgba(11,9,8,.6)" }}>{product.category}</span>
        )}
        {product.fan_favorite && (
          <span
            className="absolute top-3 right-3 font-caps inline-flex items-center gap-1"
            style={{ fontSize: ".5rem", letterSpacing: ".14em", color: "var(--ink)", background: "linear-gradient(135deg, var(--gold-lift), var(--gold))", padding: ".3rem .6rem", borderRadius: "var(--radius)", boxShadow: "0 6px 18px rgba(200,155,60,.35)" }}
            data-testid={`fan-favorite-${product.id}`}
          >
            <Star size={10} fill="currentColor" /><span className="hidden sm:inline">Fan Favorite</span>
          </span>
        )}
      </div>

      <div className="px-2 pt-4 pb-2 flex flex-col flex-1">
        <h3 className="font-body" style={{ color: "var(--parchment)", fontSize: "1rem", lineHeight: 1.3 }}>{product.name}</h3>

        {showVariants && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid={`sizes-${product.id}`}>
            {variants.map((v) => {
              const active = selectedCode === v.code;
              return (
                <button
                  key={v.code}
                  onClick={() => setSelectedCode(v.code)}
                  className="font-caps"
                  style={{
                    fontSize: ".58rem",
                    padding: ".35rem .6rem",
                    borderRadius: "var(--radius)",
                    border: `1px solid ${active ? "var(--gold)" : "rgba(200,155,60,.3)"}`,
                    color: active ? "var(--ink)" : "var(--parchment)",
                    background: active ? "var(--gold)" : "transparent",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: ".4rem",
                  }}
                  data-testid={`size-${product.id}-${v.code}`}
                >
                  <span>{v.label}</span>
                  {uniquePrices.size > 1 && (
                    <span style={{ opacity: active ? 0.85 : 0.6 }}>${v.price}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {product.fit_note && (
          <p className="mt-2 flex items-center gap-1.5 font-caps" style={{ fontSize: ".55rem", color: "var(--gold)", letterSpacing: ".08em" }} data-testid={`fit-note-${product.id}`}>
            <Ruler size={11} strokeWidth={1.75} /> {product.fit_note}
          </p>
        )}

        {showColors && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid={`colors-${product.id}`}>
            {colors.map((c) => {
              const active = selectedColor === c;
              const hex = COLOR_HEX[c] || "#888";
              return (
                <button
                  key={c}
                  onClick={() => setSelectedColor(c)}
                  aria-label={c}
                  title={c}
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: hex,
                    border: `2px solid ${active ? "var(--gold)" : "rgba(200,155,60,.3)"}`,
                    boxShadow: active ? "0 0 0 2px rgba(200,155,60,.2)" : "inset 0 0 0 1px rgba(0,0,0,.3)",
                    cursor: "pointer",
                    padding: 0,
                  }}
                  data-testid={`color-${product.id}-${c.replace(/\s+/g, "-")}`}
                />
              );
            })}
            {selectedColor && (
              <span className="font-caps text-ash self-center" style={{ fontSize: ".58rem" }}>{selectedColor}</span>
            )}
          </div>
        )}

        <div className="flex flex-col items-stretch sm:flex-row sm:items-center sm:justify-between gap-2 mt-4 pt-3" style={{ borderTop: "var(--rule)" }}>
          <div className="flex flex-col leading-tight shrink-0">
            {startingFrom && <span className="font-caps text-ash" style={{ fontSize: ".5rem" }}>from</span>}
            <span className="price-tag" style={{ fontSize: "1.5rem" }}>${displayPrice}</span>
          </div>
          <div className="flex items-center gap-2 justify-end">
            <ShareButton compact kind="merch" id={product.id} title={product.name} text="Official HeartNSoul Records merch" />
            {isExternal ? (
              <a
                className="btn-gold whitespace-nowrap justify-center flex-1 sm:flex-none"
                style={{ padding: ".6rem .9rem" }}
                href={product.fulfillment_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`buy-now-${product.id}`}
              >
                <ExternalLink size={13} /> Buy now
                <span className="sr-only">(opens in a new tab)</span>
              </a>
            ) : (
              <button className="btn-gold whitespace-nowrap justify-center flex-1 sm:flex-none" style={{ padding: ".6rem .9rem" }} onClick={onAdd} data-testid={`add-to-cart-${product.id}`}><Plus size={13} /> Add to cart</button>
            )}
          </div>
        </div>
        {(showVariants || showColors) && !isExternal && <p className="mt-3" style={{ color: "var(--ash)", fontSize: ".68rem" }}>Ships in 3-7 business days</p>}
        {isExternal && <p className="mt-3" style={{ color: "var(--ash)", fontSize: ".68rem" }}>Fulfilled by our print partner</p>}
      </div>
    </div>
  );
};
